import { useEffect, useState } from 'react';
import styles from './AiRecommendations.module.css';
import LoadingSpinner from '../../../../shared/components/LoadingSpinner';
import ActorDetailModal from '../../components/ActorDetailModal';
import { getTalentDetail } from '../../../../api/talentApi';
import { AI_RECOMMEND_MOCK } from '../../../../api/recommendationApi';
import { isBusy } from './recommendationModel';
import { useRecommendations } from './useRecommendations';
import RoleRecommendations from './RoleRecommendations';

const GENDER_KO = { MALE: '남성', FEMALE: '여성' };

/** 추천 행 → 배우 상세 모달이 받는 모양. 상세를 못 불러와도 가진 정보로 연다. */
function modalActorFrom(actor, full) {
  const base = {
    id: actor.id,
    talentProfileId: actor.id,
    slug: actor.slug,
    name: actor.name,
    gender: GENDER_KO[actor.gender] || actor.gender || '-',
    age: actor.age != null ? `${actor.age}세` : '-',
    height: actor.heightCm ? `${actor.heightCm}cm` : '-',
    weight: actor.weightKg ? `${actor.weightKg}kg` : '-',
    tags: actor.skills,
    profileImageUrl: actor.photoUrl,
  };
  if (!full) return base;
  return {
    ...full,
    ...base,
    name: full.stageName || full.name || actor.name,
    realName: full.name,
    stageName: full.stageName || '',
    gender: GENDER_KO[full.gender] || full.gender || base.gender,
    height: full.heightCm ? `${full.heightCm}cm` : base.height,
    weight: full.weightKg ? `${full.weightKg}kg` : base.weight,
    tags: (full.skills || []).map(s => s.name || s.skillName || s).filter(Boolean),
    profileImageUrl: full.profileImageUrl ?? base.profileImageUrl,
  };
}

/**
 * 지원자 관리 화면의 'AI 추천 배우' 탭.
 * 배역 탭 → 기준 범례 → 순위 목록. 기능을 쓸 수 없는 공고면 onUnavailable을 부르고 아무것도 그리지 않는다.
 */
export default function AiRecommendations({ postId, onUnavailable }) {
  const { roles, loading, loadError, unavailable, timedOut, refreshState, refresh, reload } = useRecommendations(postId);
  const [roleId, setRoleId] = useState(null);
  const [detail, setDetail] = useState(null); // { actor, rows, list }
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (unavailable && onUnavailable) onUnavailable();
  }, [unavailable, onUnavailable]);

  useEffect(() => {
    if (roles.length && !roles.some(r => r.roleId === roleId)) setRoleId(roles[0].roleId);
  }, [roles, roleId]);

  // rows: 지금 보고 있는 배역의 추천 행 — 모달에서 ←/→로 넘겨 볼 순서
  const openActor = async (actor, rows) => {
    const list = rows.map(a => modalActorFrom(a));
    if (AI_RECOMMEND_MOCK) {
      setDetail({ actor: modalActorFrom(actor), rows, list });
      return;
    }
    setDetailLoading(true);
    try {
      const full = await getTalentDetail(actor.id);
      setDetail({ actor: modalActorFrom(actor, full), rows, list });
    } catch {
      setDetail({ actor: modalActorFrom(actor), rows, list });
    } finally {
      setDetailLoading(false);
    }
  };

  if (unavailable) return null;

  if (loading) {
    return <div className={styles.loading}><LoadingSpinner size="md" /></div>;
  }
  if (loadError) {
    return (
      <div className={styles.stateBox}>
        <div className={styles.stateTitle}>추천을 불러오지 못했어요</div>
        <button type="button" className={styles.secondaryBtn} onClick={reload}>다시 시도</button>
      </div>
    );
  }
  if (roles.length === 0) {
    return (
      <div className={styles.stateBox}>
        <div className={styles.stateTitle}>추천할 배역이 없어요</div>
        <div className={styles.stateDesc}>공고에 배역을 추가하면 배역마다 어울리는 배우를 골라 드려요.</div>
      </div>
    );
  }

  const current = roles.find(r => r.roleId === roleId) ?? roles[0];

  return (
    <div className={styles.container}>
      {detailLoading && (
        <div className={styles.detailLoading}><div className={styles.detailLoadingBox}><LoadingSpinner size="sm" /></div></div>
      )}
      {detail && (
        <ActorDetailModal
          actor={detail.actor}
          actorList={detail.list}
          onNavigate={next => {
            const row = detail.rows.find(a => a.id === next.talentProfileId);
            if (row) openActor(row, detail.rows);
          }}
          onClose={() => setDetail(null)}
        />
      )}

      <p className={styles.intro}>
        배역 설명으로 후보를 추린 뒤, AI가 기준별로 사진과 프로필을 평가해 순서를 매겼어요. 막대 길이는 기준별 점수에 가중치를 곱한 몫이에요.
      </p>

      {roles.length > 1 && (
        <div className={styles.rolePills} role="tablist" aria-label="배역">
          {roles.map(r => (
            <button
              key={r.roleId}
              type="button"
              role="tab"
              aria-selected={r.roleId === current.roleId}
              className={`${styles.rolePill} ${r.roleId === current.roleId ? styles.rolePillActive : ''}`}
              onClick={() => setRoleId(r.roleId)}
            >
              {r.roleName}
              {isBusy(r.status) && <span className={styles.rolePillDot} aria-label="계산 중" />}
            </button>
          ))}
        </div>
      )}

      <RoleRecommendations
        key={current.roleId}
        role={current}
        refreshState={refreshState[current.roleId]}
        timedOut={timedOut}
        onRefresh={refresh}
        onReload={reload}
        onOpenActor={openActor}
      />
    </div>
  );
}
